import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { EmSwash } from "../components/EmSwash";
import { useNoIndex } from "../lib/useNoIndex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "mt-2 w-full rounded-md border border-border bg-sand px-4 py-3 font-serif text-base text-ink placeholder:text-ink/30 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10";
const labelCls = "text-[13px] font-semibold uppercase tracking-[0.22em] text-olive";

/** /roll/add/:token — one-tap "add my name" for monthly donors (link from the month-two note). */
export default function RollAdd() {
  useNoIndex();
  const { token } = useParams();
  const [state, setState] = useState(null); // invite state
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("loading"); // loading | ready | saving | done | error

  useEffect(() => {
    axios
      .get(`${API}/sustaining-donors/invite/${token}`)
      .then((r) => {
        setState(r.data);
        setName(r.data.display_name || r.data.suggested_name || "");
        setCity(r.data.city || "");
        setStatus("ready");
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "This link is not valid.");
        setStatus("error");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || status === "saving") return;
    setStatus("saving");
    try {
      const r = await axios.post(`${API}/sustaining-donors/invite/${token}`, { display_name: name.trim(), city: city.trim() || null });
      setState(r.data);
      setStatus("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "That didn't save. Please try again.");
      setStatus("ready");
    }
  };

  return (
    <main data-testid="roll-add-page" className="bg-sand">
      <Navbar onStart={() => (window.location.href = "/")} onDonate={() => (window.location.href = "/")} primaryLabel="Back to home" secondaryLabel={null} />
      <section className="mx-auto max-w-7xl px-6 pb-4 pt-32 lg:px-8 lg:pt-48">
        <FadeUp>
          <h1 data-testid="roll-add-headline" className="max-w-4xl font-serif text-[3.5rem] lg:text-[5.5rem] leading-[0.95] tracking-tight text-ink">
            {status === "done" ? <>You're on the <EmSwash>roll.</EmSwash></> : <>Add your name to the <EmSwash>roll.</EmSwash></>}
          </h1>
        </FadeUp>
        <div className="mt-8 h-px w-full bg-ink/15" />

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
          <FadeUp>
            <p className="max-w-md font-serif text-3xl leading-relaxed text-ink" data-testid="roll-add-lead">
              {status === "error"
                ? "This link isn't working."
                : `${state?.donor_first_name && state.donor_first_name !== "there" ? state.donor_first_name + ", " : ""}your monthly gift keeps the fund ready before a student asks. The roll is a quiet thank-you for that.`}
            </p>
            <p className="mt-6 max-w-md text-base leading-relaxed text-olive">
              Names only, never amounts. Choose how yours reads; a city is optional. You can come back to this link for 30 days to change it, and it comes off automatically if your gift ends.
            </p>
          </FadeUp>

          <FadeUp delay={0.08}>
            <div className="rounded-2xl border-2 border-ink/10 bg-bone p-6 shadow-sm lg:p-10" data-testid="roll-add-card">
              {status === "loading" && <Loader2 className="h-5 w-5 animate-spin text-olive" />}
              {status === "error" && (
                <div data-testid="roll-add-error">
                  <p className="text-base text-ink">{error}</p>
                  <p className="mt-4 text-[13px] text-olive">
                    Reply to the email this link came from, or <Link to="/contact" className="font-semibold text-ink underline decoration-tangerine decoration-2 underline-offset-4">send us a message</Link> with the name you'd like shown and we'll add it for you.
                  </p>
                </div>
              )}
              {(status === "ready" || status === "saving") && (
                <form onSubmit={submit} className="flex flex-col gap-5" data-testid="roll-add-form">
                  <label className="block">
                    <span className={labelCls}>Name shown on the roll</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required placeholder="Marcus T." data-testid="roll-add-name" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>City <span className="ml-1.5 normal-case tracking-normal text-olive/60">(optional)</span></span>
                    <input value={city} onChange={(e) => setCity(e.target.value)} maxLength={60} placeholder="Atlanta, GA" data-testid="roll-add-city" className={inputCls} />
                  </label>
                  {error && <p role="alert" className="text-[13px] text-scarlet">{error}</p>}
                  <button type="submit" disabled={status === "saving"} data-testid="roll-add-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60 sm:w-auto">
                    {status === "saving" ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <>{state?.on_roll ? "Update my entry" : "Add my name"}<ArrowRight className="h-4 w-4" strokeWidth={2} /></>}
                  </button>
                </form>
              )}
              {status === "done" && (
                <div className="flex flex-col items-start gap-4" data-testid="roll-add-success">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-grass/15 text-grass"><Check className="h-6 w-6" strokeWidth={2.4} /></span>
                  <p className="font-serif text-3xl text-ink">Listed as <span className="font-semibold">{state?.display_name}</span>{state?.city ? `, ${state.city}` : ""}.</p>
                  <p className="text-[13px] text-olive">Thank you for letting other donors and students see who keeps the fund ready.</p>
                  <div className="mt-2 flex flex-wrap gap-4">
                    <Link to="/#sustaining" data-testid="roll-add-view-link" className="group inline-flex items-center gap-1.5 text-base font-semibold text-ink hover:text-tangerine">See the roll <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} /></Link>
                    <button type="button" onClick={() => setStatus("ready")} data-testid="roll-add-edit-again" className="text-base font-semibold text-olive hover:text-ink">Change it</button>
                  </div>
                </div>
              )}
            </div>
          </FadeUp>
        </div>
      </section>

      <Footer variant="minimal" />
    </main>
  );
}
